// §4.3 Forecast editor (`/planning/forecasts/{id}`): a product by week grid of
// one forecast's lines (design-system §3.14), its header controls, Activate and
// Archive. Edits are held until Save, which sends only the products changed, a
// window at a time and at most 2 000 rows a request (D-349 ruling 16).

import { useEffect, useMemo, useState } from "react";
import type { TFunction } from "i18next";
import { useBlocker, useNavigate, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  BucketGrid,
  type BucketGridCell,
  type BucketGridLabels,
  type BucketGridRow,
  type BucketGridWeek,
} from "../../../../components/bucket-grid";
import {
  addDaysIso,
  mondaysBetween,
  parsePasteText,
  planPaste,
  sameQty,
  type PastePlan,
  type PasteProblemKind,
} from "../../../../components/bucket-grid-paste";
import { Button } from "../../../../components/button";
import { Field } from "../../../../components/field";
import { Select } from "../../../../components/select";
import { ErrorState } from "../../../../components/states";
import { StatusChip } from "../../../../components/status-chip";
import { Banner } from "../../../../components/toast";
import { useDesktop } from "../../../../components/use-desktop";
import { UnauthorizedError } from "../../../../lib/problem";
import { RequestError, requestIdOf } from "../../../../lib/request";
import { ProductPicker } from "../components/product-picker";
import { usePlanningPermissions } from "../hooks/permissions";
import {
  FORECAST_LINES_MAX,
  FORECAST_LINE_NOTE_MAX,
  patchForecast,
  problemCode,
  problemExtra,
  putForecastLines,
  resolveProductKeys,
  useForecastWindow,
  usePlanningRefresh,
  useProductIndex,
  useUoms,
  type Forecast,
  type ForecastDetail,
  type ForecastPatch,
  type Product,
} from "../hooks/planning-queries";
import { currentMonday, formatDate, parseWhole, useOffline, weekLabel } from "../lib/planning-lib";
import { PlanningHeader } from "../components/planning-header";
import { usePlanningFacility } from "../planning-layout";
import { FORECAST_STATUS_VARIANT } from "./forecasts";
import { shortId } from "../../../../lib/short-id";
import { enumLabel } from "../../../../lib/enum-label";

interface SavedCell {
  readonly qty: string;
  readonly note: string | null;
}

interface EditCell {
  readonly qty: string | null;
  readonly note: string | null;
}

type Grid<T> = ReadonlyMap<string, ReadonlyMap<string, T>>;

const key = (productId: string, week: string) => `${productId}|${week}`;

const OK_NOTICES: ReadonlySet<string> = new Set([
  "planning.editor.detailsSavedNotice",
  "planning.editor.activatedNotice",
]);

function savedGrid(detail: ForecastDetail | undefined): Grid<SavedCell> {
  const out = new Map<string, Map<string, SavedCell>>();
  for (const line of detail?.lines ?? []) {
    const row = out.get(line.product_id) ?? new Map<string, SavedCell>();
    row.set(line.bucket_start, { qty: line.qty, note: line.note });
    out.set(line.product_id, row);
  }
  return out;
}

function withEdit(
  edits: Grid<EditCell>,
  productId: string,
  week: string,
  cell: EditCell,
): Grid<EditCell> {
  const next = new Map(edits);
  const row = new Map(next.get(productId) ?? []);
  row.set(week, cell);
  next.set(productId, row);
  return next;
}

/** The value a cell shows: the planner's edit when there is one, else the saved line. */
export function shownCell(
  saved: Grid<SavedCell>,
  edits: Grid<EditCell>,
  productId: string,
  week: string,
): EditCell {
  const edit = edits.get(productId)?.get(week);
  if (edit !== undefined) return edit;
  const line = saved.get(productId)?.get(week);
  return line !== undefined ? { qty: line.qty, note: line.note } : { qty: null, note: null };
}

/** Products whose edits differ from what is saved. */
export function dirtyProducts(saved: Grid<SavedCell>, edits: Grid<EditCell>): string[] {
  const out: string[] = [];
  for (const [productId, row] of edits) {
    for (const [week, edit] of row) {
      const line = saved.get(productId)?.get(week);
      const savedQty = line?.qty ?? null;
      const savedNote = line?.note ?? null;
      if (
        !sameQty(edit.qty, savedQty) ||
        (edit.qty !== null && (edit.note ?? null) !== savedNote)
      ) {
        out.push(productId);
        break;
      }
    }
  }
  return out;
}

/** Splits the edits at `monday`: the weeks before it are read-only and cannot be saved. */
export function editsFrom(
  edits: Grid<EditCell>,
  monday: string,
): { kept: Grid<EditCell>; dropped: number } {
  const kept = new Map<string, ReadonlyMap<string, EditCell>>();
  let dropped = 0;
  for (const [productId, row] of edits) {
    const next = new Map<string, EditCell>();
    for (const [week, cell] of row) {
      if (week < monday) dropped += 1;
      else next.set(week, cell);
    }
    if (next.size > 0) kept.set(productId, next);
  }
  return { kept, dropped };
}

export interface LinesRequest {
  readonly productIds: string[];
  readonly lines: { product_id: string; bucket_start: string; qty: string; note: string | null }[];
}

/**
 * Groups the changed products into requests of at most `max` rows, each row a
 * week on or after `from` that holds a value. A product never splits across
 * requests, because each request replaces its products' lines in the window.
 */
export function buildLineRequests(
  productIds: readonly string[],
  weeks: readonly string[],
  shown: (productId: string, week: string) => EditCell,
  max: number = FORECAST_LINES_MAX,
): LinesRequest[] {
  const out: LinesRequest[] = [];
  let current: LinesRequest = { productIds: [], lines: [] };
  for (const productId of productIds) {
    const rows = weeks.flatMap((week) => {
      const cell = shown(productId, week);
      return cell.qty === null || cell.qty === ""
        ? []
        : [{ product_id: productId, bucket_start: week, qty: cell.qty, note: cell.note }];
    });
    if (current.productIds.length > 0 && current.lines.length + rows.length > max) {
      out.push(current);
      current = { productIds: [], lines: [] };
    }
    current.productIds.push(productId);
    current.lines.push(...rows);
  }
  if (current.productIds.length > 0) out.push(current);
  return out;
}

interface SaveReport {
  readonly saved: string[];
  readonly refused: string[];
  readonly stopped?: "changed" | "archived" | "weekRolled" | "failed";
  readonly requestId?: string | undefined;
  /** Edits to a week that turned read-only before the save were not sent. */
  readonly pastDropped?: boolean;
}

/** A 400 that says the save reached back before the facility's current week. */
function beforeCurrentWeek(error: unknown): boolean {
  if (!(error instanceof RequestError) || error.problem === undefined) return false;
  const errors = (error.problem as { errors?: { path?: string; code?: string }[] }).errors;
  return (errors ?? []).some((e) => e.path === "bucket_from" && e.code === "before_current_week");
}

export function PlanningForecastEditorScreen() {
  const forecastId = useParams({ strict: false }).forecastId ?? "";
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const navigate = useNavigate();
  const desktop = useDesktop();
  const offline = useOffline();
  const perms = usePlanningPermissions();
  const { facility, facilities, selectFacility } = usePlanningFacility();
  const refresh = usePlanningRefresh();

  const [window, setWindow] = useState<{ from: string; to: string } | undefined>(undefined);
  const detail = useForecastWindow(forecastId, window);
  const [todayMonday, setTodayMonday] = useState<string | undefined>(undefined);
  const [header, setHeader] = useState<Forecast | undefined>(undefined);
  const [edits, setEdits] = useState<Grid<EditCell>>(new Map());
  const [added, setAdded] = useState<readonly string[]>([]);
  const [refusals, setRefusals] = useState<ReadonlyMap<string, string>>(new Map());
  const [focus, setFocus] = useState<{ productId: string; week: string } | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [report, setReport] = useState<SaveReport | undefined>(undefined);
  const [pasteReport, setPasteReport] = useState<PastePlan | undefined>(undefined);
  const [headerNotice, setHeaderNotice] = useState<string | undefined>(undefined);
  const [nameProblem, setNameProblem] = useState<string | undefined>(undefined);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [headerPending, setHeaderPending] = useState(false);
  const [draftHeader, setDraftHeader] = useState<
    { name: string; mode: Forecast["consumption_mode"]; window: string } | undefined
  >(undefined);

  const data = detail.data;
  useEffect(() => {
    if (data === undefined) return;
    // The server's default window starts at the facility's current Monday.
    if (window === undefined && todayMonday === undefined) setTodayMonday(data.bucket_from);
    setHeader((h) =>
      h === undefined || h.updated_at <= data.forecast.updated_at ? data.forecast : h,
    );
  }, [data, window, todayMonday]);

  useEffect(() => {
    if (header !== undefined && draftHeader === undefined) {
      setDraftHeader({
        name: header.name,
        mode: header.consumption_mode,
        window: String(header.consumption_window_days),
      });
    }
  }, [header, draftHeader]);

  useEffect(() => {
    if (
      header !== undefined &&
      header.facility_id !== facility.id &&
      facilities.some((f) => f.id === header.facility_id)
    ) {
      selectFacility(header.facility_id);
    }
  }, [header, facility.id, facilities, selectFacility]);

  useEffect(() => {
    if (detail.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [detail.error, navigate]);

  const saved = useMemo(() => savedGrid(data), [data]);
  const dirty = useMemo(() => dirtyProducts(saved, edits), [saved, edits]);

  const leaving = useBlocker({
    shouldBlockFn: () => dirty.length > 0,
    enableBeforeUnload: () => dirty.length > 0,
    withResolver: true,
  });

  const from = data?.bucket_from;
  const to = data?.bucket_to;
  const weeks = from !== undefined && to !== undefined ? mondaysBetween(from, to) : [];
  const spanWeeks = weeks.length > 0 ? weeks.length : 1;
  const archived = header?.status === "archived";
  const canEdit = perms.has("planning.demand.manage") && !archived && desktop;
  const writable = canEdit && !offline && !saving;
  // The server's first answer anchors the current week; the facility's clock
  // moves it on when the editor stays open past Monday midnight.
  const zone = facilities.find((f) => f.id === header?.facility_id)?.timezone ?? facility.timezone;
  const liveMonday = (): string | undefined => {
    if (todayMonday === undefined) return undefined;
    const now = currentMonday(zone);
    return now > todayMonday ? now : todayMonday;
  };
  const thisMonday = liveMonday();
  useEffect(() => {
    if (todayMonday === undefined) return undefined;
    const tick = setInterval(() => {
      const now = currentMonday(zone);
      if (now > todayMonday) setTodayMonday(now);
    }, 60_000);
    return () => {
      clearInterval(tick);
    };
  }, [todayMonday, zone]);
  const pastWeeks = new Set(weeks.filter((w) => thisMonday !== undefined && w < thisMonday));

  const rowIds = useMemo(() => {
    const ids: string[] = [];
    for (const id of saved.keys()) ids.push(id);
    for (const id of added) if (!ids.includes(id)) ids.push(id);
    for (const id of edits.keys()) if (!ids.includes(id)) ids.push(id);
    return ids;
  }, [saved, added, edits]);
  const products = useProductIndex(rowIds);
  const uoms = useUoms();
  const uomCode = (product: Product | undefined) =>
    product === undefined
      ? ""
      : (uoms.data?.items.find((u) => u.id === product.base_uom_id)?.code ?? "");

  const sortedIds = [...rowIds].sort((a, b) => {
    const pa = products.byId.get(a)?.sku ?? a;
    const pb = products.byId.get(b)?.sku ?? b;
    return pa.localeCompare(pb);
  });

  const gridWeeks: BucketGridWeek[] = weeks.map((w) => ({
    start: w,
    label: weekLabel(w, locale),
    readOnly: pastWeeks.has(w),
  }));

  const gridRows: BucketGridRow[] = sortedIds.map((productId) => {
    const product = products.byId.get(productId);
    const cells: Record<string, BucketGridCell> = {};
    for (const week of weeks) {
      const shown = shownCell(saved, edits, productId, week);
      const line = saved.get(productId)?.get(week);
      const edited =
        edits.get(productId)?.get(week) !== undefined && !sameQty(shown.qty, line?.qty ?? null);
      const refusal = refusals.get(key(productId, week));
      cells[week] = {
        value: shown.qty,
        ...(edited ? { edited: true } : {}),
        ...(refusal !== undefined ? { refusal } : {}),
      };
    }
    const unit = uomCode(product);
    return {
      key: productId,
      labelText: product?.sku ?? shortId(productId),
      label: (
        <span className="planning-product">
          <span className="planning-sku">{product?.sku ?? shortId(productId)}</span>
          {product !== undefined ? (
            <span className="planning-product-name">{product.name}</span>
          ) : null}
          {unit !== "" ? <span className="planning-unit">{unit}</span> : null}
        </span>
      ),
      cells,
    };
  });

  const labels: BucketGridLabels = gridLabels(t);

  const setCell = (productId: string, week: string, qty: string | null, note?: string | null) => {
    const current = shownCell(saved, edits, productId, week);
    setEdits((e) =>
      withEdit(e, productId, week, {
        qty,
        note: qty === null ? null : note !== undefined ? note : current.note,
      }),
    );
    setRefusals((r) => {
      const next = new Map(r);
      next.delete(key(productId, week));
      return next;
    });
    setReport(undefined);
  };

  const moveWindow = (direction: -1 | 1) => {
    if (from === undefined || to === undefined) return;
    const shift = direction * 7 * spanWeeks;
    setWindow({ from: addDaysIso(from, shift), to: addDaysIso(to, shift) });
  };

  const onPaste = async (text: string, anchor: { rowIndex: number; weekIndex: number }) => {
    const block = parsePasteText(text, locale);
    const resolved = new Map<string, string>();
    const unresolved = new Map<string, PasteProblemKind>();
    if (block.productKeys !== undefined) {
      const pending: string[] = [];
      for (const k of block.productKeys) {
        const lower = k.trim().toLocaleLowerCase();
        const known = [...products.byId.values()].find(
          (p) => p.sku.toLocaleLowerCase() === lower || p.name.toLocaleLowerCase() === lower,
        );
        if (known !== undefined) resolved.set(lower, known.id);
        else if (lower.length > 0) pending.push(k);
      }
      if (pending.length > 0) {
        const keys = await resolveProductKeys(pending);
        for (const [lower, product] of keys.found) resolved.set(lower, product.id);
        for (const lower of keys.failed) unresolved.set(lower, "lookup_failed");
        for (const lower of keys.overLimit) unresolved.set(lower, "over_key_limit");
      }
    }
    const plan = planPaste(block, {
      weeks,
      readOnlyWeeks: pastWeeks,
      rowKeys: sortedIds,
      anchorRow: anchor.rowIndex,
      anchorWeek: anchor.weekIndex,
      resolveRow: (k) => resolved.get(k.trim().toLocaleLowerCase()),
      unresolvedKind: (k) => unresolved.get(k.trim().toLocaleLowerCase()),
    });
    if (plan.newRowKeys.length > 0)
      setAdded((a) => [...a, ...plan.newRowKeys.filter((id) => !a.includes(id))]);
    let next = edits;
    for (const w of plan.writes) {
      const current = shownCell(saved, next, w.rowKey, w.week);
      next = withEdit(next, w.rowKey, w.week, { qty: w.value, note: current.note });
    }
    setEdits(next);
    setPasteReport(plan);
    setReport(undefined);
  };

  const save = async () => {
    const monday = liveMonday();
    if (header === undefined || from === undefined || to === undefined || monday === undefined)
      return;
    if (monday !== todayMonday) setTodayMonday(monday);
    const saveFrom = from < monday ? monday : from;
    if (saveFrom > to) return;
    const saveWeeks = mondaysBetween(saveFrom, to);
    const live = editsFrom(edits, saveFrom);
    const requests = buildLineRequests(dirtyProducts(saved, live.kept), saveWeeks, (p, w) =>
      shownCell(saved, live.kept, p, w),
    );
    setSaving(true);
    setReport(undefined);
    const savedIds: string[] = [];
    const refusedIds: string[] = [];
    const newRefusals = new Map(refusals);
    let expected = header.updated_at;
    let stopped: SaveReport["stopped"];
    let requestId: string | undefined;
    let keptFrom = saveFrom;
    for (const request of requests) {
      try {
        const answer = await putForecastLines(header.id, {
          bucket_from: saveFrom,
          bucket_to: to,
          product_ids: request.productIds,
          lines: request.lines.map((l) => ({ ...l, note: l.note ?? null })),
          expected_updated_at: expected,
        });
        expected = answer.forecast.updated_at;
        setHeader(answer.forecast);
        savedIds.push(...request.productIds);
      } catch (error) {
        if (error instanceof UnauthorizedError) {
          setSaving(false);
          void navigate({ to: "/login" });
          return;
        }
        const code = problemCode(error);
        if (code === "FORECAST_LINES_INVALID") {
          for (const row of (problemExtra(error, "rows") as
            { index: number; codes: string[] }[] | undefined) ?? []) {
            const line = request.lines[row.index];
            if (line === undefined) continue;
            newRefusals.set(
              key(line.product_id, line.bucket_start),
              row.codes.map((c) => enumLabel(t, "planning.editor.rowCodes", c)).join(". "),
            );
          }
          refusedIds.push(...request.productIds);
          continue;
        }
        refusedIds.push(...request.productIds);
        if (beforeCurrentWeek(error)) {
          // The server's week has moved past ours: the next save starts a week later.
          stopped = "weekRolled";
          keptFrom = addDaysIso(saveFrom, 7);
          setTodayMonday(keptFrom);
        } else if (code === "FORECAST_CHANGED") stopped = "changed";
        else if (code === "FORECAST_ARCHIVED") stopped = "archived";
        else {
          stopped = "failed";
          requestId = requestIdOf(error);
        }
        const current = problemExtra(error, "current") as Forecast | undefined;
        if (current !== undefined) setHeader(current);
        break;
      }
    }
    // Edits of the saved products are now the saved lines.
    const before = saved;
    const kept = editsFrom(live.kept, keptFrom);
    const remaining = new Map(kept.kept);
    for (const id of savedIds) remaining.delete(id);
    const reloaded = await detail.refetch();
    const after = savedGrid(reloaded.data);
    if (stopped === "changed") {
      // Keep the planner's edits and mark those whose saved value moved underneath.
      for (const [productId, row] of remaining) {
        for (const week of row.keys()) {
          const was = before.get(productId)?.get(week)?.qty ?? null;
          const now = after.get(productId)?.get(week)?.qty ?? null;
          if (!sameQty(was, now))
            newRefusals.set(key(productId, week), t("planning.editor.changedUnderneath"));
        }
      }
    }
    for (const id of savedIds) {
      for (const k of [...newRefusals.keys()]) if (k.startsWith(`${id}|`)) newRefusals.delete(k);
    }
    setEdits(remaining);
    setAdded((a) => a.filter((id) => !savedIds.includes(id) || after.has(id)));
    setRefusals(newRefusals);
    setReport({
      saved: savedIds,
      refused: refusedIds,
      ...(stopped !== undefined ? { stopped } : {}),
      requestId,
      ...(live.dropped + kept.dropped > 0 && stopped !== "weekRolled" ? { pastDropped: true } : {}),
    });
    setSaving(false);
    void refresh.forecasts(facility.id);
  };

  const patchHeader = async (patch: Omit<ForecastPatch, "expected_updated_at">, notice: string) => {
    if (header === undefined) return;
    setHeaderPending(true);
    setHeaderNotice(undefined);
    setNameProblem(undefined);
    try {
      const next = await patchForecast(header.id, {
        ...patch,
        expected_updated_at: header.updated_at,
      });
      setHeader(next);
      setDraftHeader({
        name: next.name,
        mode: next.consumption_mode,
        window: String(next.consumption_window_days),
      });
      setHeaderNotice(notice);
      void refresh.forecasts(facility.id);
    } catch (error) {
      if (error instanceof UnauthorizedError) {
        void navigate({ to: "/login" });
        return;
      }
      const code = problemCode(error);
      const current = problemExtra(error, "current") as Forecast | undefined;
      if (current !== undefined) {
        setHeader(current);
        setDraftHeader({
          name: current.name,
          mode: current.consumption_mode,
          window: String(current.consumption_window_days),
        });
      }
      if (code === "FORECAST_NAME_TAKEN") setNameProblem("planning.forecasts.errors.nameTaken");
      else if (code === "FORECAST_CHANGED") setHeaderNotice("planning.common.changedBySomeone");
      else if (code === "FORECAST_ARCHIVED") {
        setHeaderNotice("planning.editor.archivedNotice");
        void detail.refetch();
      } else if (code === "FORECAST_STATUS_TRANSITION_INVALID")
        setHeaderNotice("planning.editor.transitionInvalid");
      else if (error instanceof RequestError && error.httpStatus === 400)
        setHeaderNotice("planning.editor.headerInvalid");
      else setHeaderNotice("planning.common.saveFailedShort");
    } finally {
      setHeaderPending(false);
      setConfirmArchive(false);
    }
  };

  if (detail.error !== null && data === undefined) {
    const notFound = detail.error instanceof RequestError && detail.error.httpStatus === 404;
    return (
      <section className="planning-screen" data-testid="planning-forecast-editor">
        <PlanningHeader
          title={t("planning.editor.title")}
          crumbs={[{ label: t("planning.forecasts.title"), to: "/planning/forecasts" }]}
        />
        <ErrorState
          title={notFound ? t("planning.editor.notFound") : t("planning.editor.loadError")}
          retryLabel={t("planning.common.retry")}
          onRetry={() => void detail.refetch()}
          requestId={requestIdOf(detail.error)}
          requestIdLabel={t("planning.common.requestId")}
        />
      </section>
    );
  }

  const focusedCell =
    focus !== undefined ? shownCell(saved, edits, focus.productId, focus.week) : undefined;
  const focusedWritable = focus !== undefined && writable && !pastWeeks.has(focus.week);
  const headerWritable =
    perms.has("planning.demand.manage") && !archived && desktop && !offline && !headerPending;

  return (
    <section className="planning-screen" data-testid="planning-forecast-editor">
      <PlanningHeader
        title={header?.name ?? t("planning.editor.title")}
        crumbs={[{ label: t("planning.forecasts.title"), to: "/planning/forecasts" }]}
        actions={
          header !== undefined ? (
            <StatusChip
              variant={FORECAST_STATUS_VARIANT[header.status]}
              data-testid="planning-forecast-status"
              data-status={header.status}
            >
              {t(`planning.forecastStatus.${header.status}`)}
            </StatusChip>
          ) : undefined
        }
      />
      {archived ? (
        <Banner kind="warn" data-testid="planning-forecast-archived">
          {t("planning.editor.archivedBanner")}
        </Banner>
      ) : null}
      {!desktop ? <p className="planning-note">{t("planning.editor.phoneReadOnly")}</p> : null}
      {desktop && offline && perms.has("planning.demand.manage") ? (
        <p className="planning-note">{t("planning.common.offlineWrites")}</p>
      ) : null}
      {headerNotice !== undefined ? (
        <Banner
          kind={OK_NOTICES.has(headerNotice) ? "ok" : "warn"}
          data-testid="planning-forecast-header-notice"
        >
          {t(headerNotice)}
        </Banner>
      ) : null}

      {header !== undefined && draftHeader !== undefined && desktop ? (
        <div className="planning-forecast-head">
          <div className="planning-field">
            <Field label={t("planning.forecasts.fields.name")} wide>
              <input
                type="text"
                value={draftHeader.name}
                maxLength={120}
                disabled={!headerWritable}
                aria-invalid={nameProblem !== undefined || undefined}
                data-testid="planning-editor-name"
                onChange={(e) => {
                  setDraftHeader({ ...draftHeader, name: e.target.value });
                  setNameProblem(undefined);
                }}
              />
            </Field>
            {nameProblem !== undefined ? (
              <span className="field-error" role="alert" data-testid="planning-editor-name-error">
                {t(nameProblem)}
              </span>
            ) : null}
          </div>
          <Field label={t("planning.forecasts.fields.mode")}>
            <Select
              value={draftHeader.mode}
              disabled={!headerWritable}
              data-testid="planning-editor-mode"
              onChange={(e) => {
                setDraftHeader({
                  ...draftHeader,
                  mode: e.target.value as Forecast["consumption_mode"],
                });
              }}
            >
              <option value="consume">{t("planning.forecasts.modes.consume")}</option>
              <option value="additive">{t("planning.forecasts.modes.additive")}</option>
            </Select>
          </Field>
          <Field label={t("planning.forecasts.fields.window")}>
            <input
              type="text"
              inputMode="numeric"
              value={draftHeader.window}
              disabled={!headerWritable}
              data-testid="planning-editor-window"
              onChange={(e) => {
                setDraftHeader({ ...draftHeader, window: e.target.value });
              }}
            />
          </Field>
          {perms.has("planning.demand.manage") && !archived ? (
            <div className="form-actions">
              <Button
                disabled={!headerWritable}
                data-testid="planning-editor-save-details"
                onClick={() => {
                  const name = draftHeader.name.trim();
                  const days = parseWhole(draftHeader.window, 0, 91);
                  if (name.length === 0 || name.length > 120) {
                    setNameProblem("planning.forecasts.errors.name");
                    return;
                  }
                  if (days === undefined) {
                    setHeaderNotice("planning.forecasts.errors.window");
                    return;
                  }
                  const patch: Omit<ForecastPatch, "expected_updated_at"> = {};
                  if (name !== header.name) patch.name = name;
                  if (draftHeader.mode !== header.consumption_mode)
                    patch.consumption_mode = draftHeader.mode;
                  if (days !== header.consumption_window_days) patch.consumption_window_days = days;
                  if (Object.keys(patch).length === 0) return;
                  void patchHeader(patch, "planning.editor.detailsSavedNotice");
                }}
              >
                {t("planning.editor.saveDetails")}
              </Button>
              {header.status === "draft" ? (
                <Button
                  variant="primary"
                  disabled={!headerWritable || dirty.length > 0}
                  data-testid="planning-editor-activate"
                  onClick={() =>
                    void patchHeader({ status: "active" }, "planning.editor.activatedNotice")
                  }
                >
                  {t("planning.editor.activate")}
                </Button>
              ) : null}
              <Button
                variant="danger"
                disabled={!headerWritable}
                data-testid="planning-editor-archive"
                onClick={() => {
                  setConfirmArchive(true);
                }}
              >
                {t("planning.editor.archive")}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
      {header !== undefined && !desktop ? (
        <p className="planning-hint">
          {t(`planning.forecasts.modes.${header.consumption_mode}`)} ·{" "}
          {t("planning.forecasts.windowDays", { count: header.consumption_window_days })}
        </p>
      ) : null}
      {confirmArchive ? (
        <div
          className="planning-confirm"
          role="alertdialog"
          aria-label={t("planning.editor.archive")}
        >
          <p>{t("planning.editor.archiveConfirm")}</p>
          <div className="form-actions">
            <Button
              variant="danger"
              busy={headerPending}
              busyLabel={t("planning.common.saving")}
              data-testid="planning-editor-archive-confirm"
              onClick={() =>
                void patchHeader({ status: "archived" }, "planning.editor.archivedNotice")
              }
            >
              {t("planning.editor.archive")}
            </Button>
            <Button
              onClick={() => {
                setConfirmArchive(false);
              }}
            >
              {t("planning.common.cancel")}
            </Button>
          </div>
        </div>
      ) : null}

      <div className="planning-window">
        <Button
          size="sm"
          disabled={data === undefined || dirty.length > 0}
          data-testid="planning-editor-earlier"
          onClick={() => {
            moveWindow(-1);
          }}
        >
          {t("planning.editor.earlier")}
        </Button>
        <span className="planning-window-label" data-testid="planning-editor-window-label">
          {from !== undefined && to !== undefined
            ? t("planning.editor.windowRange", {
                from: formatDate(from, locale),
                to: formatDate(to, locale),
              })
            : ""}
        </span>
        <Button
          size="sm"
          disabled={data === undefined || dirty.length > 0}
          data-testid="planning-editor-later"
          onClick={() => {
            moveWindow(1);
          }}
        >
          {t("planning.editor.later")}
        </Button>
        {dirty.length > 0 ? (
          <span className="planning-hint">{t("planning.editor.saveBeforeMoving")}</span>
        ) : null}
      </div>
      {data !== undefined && data.lines_outside_window > 0 ? (
        <p className="planning-hint" data-testid="planning-editor-outside">
          {t("planning.editor.outside", { count: data.lines_outside_window })}
        </p>
      ) : null}

      {canEdit ? (
        <ProductPicker
          label={t("planning.editor.addProduct")}
          disabled={!writable}
          exclude={new Set(sortedIds)}
          data-testid="planning-editor-add"
          onPick={(p) => {
            setAdded((a) => (a.includes(p.id) ? a : [...a, p.id]));
          }}
        />
      ) : null}
      {canEdit ? <p className="planning-hint">{t("planning.editor.pasteHint")}</p> : null}

      <BucketGrid
        rows={gridRows}
        weeks={gridWeeks}
        locale={locale}
        labels={labels}
        editable={writable}
        loading={data === undefined}
        loadingLabel={t("planning.editor.loading")}
        onCommit={(productId, week, value) => {
          setCell(productId, week, value);
          return undefined;
        }}
        onPaste={(text, anchor) => void onPaste(text, anchor)}
        onFocusCell={(productId, week) => {
          setFocus({ productId, week });
        }}
        data-testid="planning-forecast-grid"
      />

      {focus !== undefined && focusedCell !== undefined && canEdit ? (
        <div className="planning-field planning-note-editor">
          <Field
            label={t("planning.editor.note", {
              product: products.byId.get(focus.productId)?.sku ?? "",
              week: weekLabel(focus.week, locale),
            })}
            wide
          >
            <input
              type="text"
              maxLength={FORECAST_LINE_NOTE_MAX}
              value={focusedCell.note ?? ""}
              disabled={!focusedWritable || focusedCell.qty === null}
              data-testid="planning-editor-note"
              onChange={(e) => {
                setCell(
                  focus.productId,
                  focus.week,
                  focusedCell.qty,
                  e.target.value === "" ? null : e.target.value,
                );
              }}
            />
          </Field>
          {focusedCell.qty === null ? (
            <span className="planning-hint">{t("planning.editor.noteNeedsQty")}</span>
          ) : null}
        </div>
      ) : null}

      {pasteReport !== undefined ? (
        <PasteReportPanel
          report={pasteReport}
          locale={locale}
          t={t}
          onDismiss={() => {
            setPasteReport(undefined);
          }}
        />
      ) : null}

      {canEdit ? (
        <div className="form-actions planning-save-bar">
          <Button
            variant="primary"
            disabled={!writable || dirty.length === 0}
            busy={saving}
            busyLabel={t("planning.common.saving")}
            data-testid="planning-editor-save"
            onClick={() => void save()}
          >
            {t("planning.editor.save", { count: dirty.length })}
          </Button>
          <Button
            disabled={saving || dirty.length === 0}
            data-testid="planning-editor-discard"
            onClick={() => {
              setEdits(new Map());
              setRefusals(new Map());
              setAdded([]);
            }}
          >
            {t("planning.editor.discard")}
          </Button>
        </div>
      ) : null}
      {report !== undefined ? (
        <SaveReportBanner report={report} products={products.byId} t={t} />
      ) : null}

      {leaving.status === "blocked" ? (
        <div
          className="planning-confirm"
          role="alertdialog"
          aria-label={t("planning.editor.leaveTitle")}
        >
          <p>{t("planning.editor.leaveBody", { count: dirty.length })}</p>
          <div className="form-actions">
            <Button variant="danger" data-testid="planning-editor-leave" onClick={leaving.proceed}>
              {t("planning.editor.leave")}
            </Button>
            <Button data-testid="planning-editor-stay" onClick={leaving.reset}>
              {t("planning.editor.stay")}
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

export function gridLabels(t: TFunction): BucketGridLabels {
  return {
    grid: t("planning.grid.label"),
    product: t("planning.grid.product"),
    editCell: (row, week) => t("planning.grid.cell", { product: row, week }),
    quantity: t("planning.grid.quantity"),
    reason: t("planning.grid.reason"),
    commit: t("planning.grid.commit"),
    cancel: t("planning.common.cancel"),
    showDetails: (row) => t("planning.grid.showDetails", { product: row }),
    hideDetails: (row) => t("planning.grid.hideDetails", { product: row }),
    frozen: t("planning.grid.frozen"),
    overridden: t("planning.grid.overridden"),
    dropped: t("planning.grid.dropped"),
    edited: t("planning.grid.edited"),
    busy: t("planning.grid.busy"),
    invalid: (reason) => t(`planning.grid.invalid.${reason}`),
    previousWeek: t("planning.grid.previousWeek"),
    nextWeek: t("planning.grid.nextWeek"),
    phoneWeek: (week) => t("planning.grid.phoneWeek", { week }),
    empty: t("planning.editor.empty"),
  };
}

function PasteReportPanel(props: {
  report: PastePlan;
  locale: string;
  t: TFunction;
  onDismiss: () => void;
}) {
  const { report, t } = props;
  return (
    <div className="planning-paste-report" role="status" data-testid="planning-paste-report">
      <p>{t("planning.paste.summary", { count: report.writes.length })}</p>
      {report.hints.length > 0 ? (
        <ul>
          {report.hints.map((h) => (
            <li key={`h${h.column}`} data-testid="planning-paste-hint">
              {t("planning.paste.snapped", {
                text: h.text,
                week: formatDate(h.monday, props.locale),
              })}
            </li>
          ))}
        </ul>
      ) : null}
      {report.problems.length > 0 ? (
        <ul>
          {report.problems.map((p) => (
            <li
              key={`p${p.line}-${p.column}`}
              className="planning-paste-problem"
              data-testid="planning-paste-problem"
            >
              {t(`planning.paste.problems.${p.kind}`, {
                line: p.line,
                column: p.column,
                text: p.text,
              })}
            </li>
          ))}
        </ul>
      ) : null}
      <Button size="sm" onClick={props.onDismiss}>
        {t("planning.common.close")}
      </Button>
    </div>
  );
}

function SaveReportBanner(props: {
  report: SaveReport;
  products: ReadonlyMap<string, Product>;
  t: TFunction;
}) {
  const { report, t } = props;
  const names = (ids: readonly string[]) =>
    ids.map((id) => props.products.get(id)?.sku ?? shortId(id)).join(", ");
  const kind =
    report.refused.length === 0 && report.stopped === undefined && report.pastDropped !== true
      ? "ok"
      : report.saved.length > 0
        ? "warn"
        : "bad";
  return (
    <Banner kind={kind} data-testid="planning-editor-report">
      {report.saved.length > 0
        ? t("planning.editor.report.saved", {
            count: report.saved.length,
            products: names(report.saved),
          })
        : null}
      {report.refused.length > 0
        ? ` ${t("planning.editor.report.refused", { count: report.refused.length, products: names(report.refused) })}`
        : null}
      {report.stopped === "changed" ? ` ${t("planning.editor.report.changed")}` : null}
      {report.stopped === "archived" ? ` ${t("planning.editor.report.archived")}` : null}
      {report.stopped === "weekRolled" ? ` ${t("planning.editor.report.weekRolled")}` : null}
      {report.pastDropped === true ? ` ${t("planning.editor.report.pastDropped")}` : null}
      {report.stopped === "failed"
        ? ` ${t("planning.common.saveFailed", { requestId: report.requestId ?? "-" })}`
        : null}
    </Banner>
  );
}
